import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import React from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { FormInput } from '../../../../components/forms/FormInput';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import useChart from '../../../../hooks/useChart';
import { Chart, PatientDoc, FileTypes, AgentRequest } from '@usacsmideo/types';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { Button } from '../../../../components/buttons/Button';
import { useMyAgents } from '../../../../hooks/useAgentRequests';
import { emailsMatch, isChartRecordComplete } from '../../../../data/agents/agentSlotConvergence';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { useInitChartSection } from '../../../../hooks/useInitChartSection';
import { Uploader } from '../../../../components/Uploader';

import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { formatPhoneE164, validatePhone } from '../../../../utils/phone';
import { isValidEmail } from '../../../../utils/isValidEmail';

import { FieldInfo } from '../../../../components/FieldInfo';

import * as Sentry from '@sentry/browser';

import { FormSelect } from '../../../../components/forms/FormSelect';

import { classNames } from '../../../../style/classNames';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import { setDisabledPage } from '../../../../data/navigation/utilities/setDisabledPage';

export const Route = createFileRoute('/_authenticated/$patientId/careTeam/hcaInfo')({
  component: HCAInfoPage,
});

function HCAInfoPage() {
  const { mutate, mutateAsync } = useUpdateChartSection();
  const { patientId } = Route.useParams();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;

  // The live HCA invite this chart record is linked to (by stamped agentRequestId),
  // if any. Used to warn before an email change orphans an active invite
  // (CU-86aj2q4fc). Matched across all statuses; "live" is checked at render.
  const { partition: agentPartition } = useMyAgents();
  const hcaLinkedRequest: AgentRequest | undefined = React.useMemo(() => {
    const id = chart?.HEALTHCARE_AGENT_INFO?.agentRequestId;
    if (!id) return undefined;
    return agentPartition.all.find((r) => r.agentType === 'HCA' && r.id === id);
  }, [chart?.HEALTHCARE_AGENT_INFO?.agentRequestId, agentPartition.all]);
  const [hcaInfoSameAsPatient, setHcaInfoSameAsPatient] = React.useState<boolean>(false);
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const [HCANomination, setHCANomination] = React.useState<string>('unset');
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();

  // REFS TO PREVENT RACE CONDITIONS DURING MUTATIONS
  const isSubmittingRef = React.useRef(false);
  const capturedFormValuesRef = React.useRef<typeof hcaInfoForm.state.values | null>(null);

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }


    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  // INITIALIZE SCRIPT_REVIEW DOCUMENT IF NONE EXISTS — ONCE PER FAILURE, NOT ONCE PER SNAPSHOT
  useInitChartSection({
    needed: !!chart && !chart.SCRIPT_REVIEW,
    write: () =>
      mutateAsync({
        patientId: patientId,
        docData: {
          SCRIPT_REVIEW: {
            attestedCode: '',
            scriptAttested: false,
            speaker: 'patient',
            telePrompterScript: '',
          },
        },
      }),
  });

  // RESET FORM VALUES WHEN CHART DATA ARRIVES
  // THE useForm HOOK ONLY EVALUATES defaultValues ONCE ON MOUNT,
  // BUT chart IS INITIALLY UNDEFINED UNTIL THE SNAPSHOT ARRIVES
  // SKIP RESET IF WE'RE IN THE MIDDLE OF A MUTATION TO PREVENT RACE CONDITIONS
  React.useEffect(() => {
    if (isSubmittingRef.current) {
      // Skip form reset while mutation is in progress
      return;
    }
    if (chart?.HEALTHCARE_AGENT_INFO) {
      const hcaInfo = chart.HEALTHCARE_AGENT_INFO;
      hcaInfoForm.setFieldValue('agent', hcaInfo.agent || '');
      hcaInfoForm.setFieldValue('street', hcaInfo.street || '');
      hcaInfoForm.setFieldValue('agentCity', hcaInfo.city || '');
      hcaInfoForm.setFieldValue('agentState', hcaInfo.state || '');
      hcaInfoForm.setFieldValue('agentZIP', hcaInfo.ZIP || '');
      hcaInfoForm.setFieldValue('agentCountry', hcaInfo.country || '');
      hcaInfoForm.setFieldValue('agentEmail', hcaInfo.email || '');
      // PHONE FIELDS: READ NEW FIELDS WITH FALLBACK TO LEGACY
      hcaInfoForm.setFieldValue(
        'primaryPhone',
        hcaInfo.primaryPhone || hcaInfo.cellPhone || '',
      );
      hcaInfoForm.setFieldValue(
        'secondaryPhone',
        hcaInfo.secondaryPhone || hcaInfo.homePhone || '',
      );
      hcaInfoForm.setFieldValue('agentToPatient', hcaInfo.agentToPatient || '');
      hcaInfoForm.setFieldValue('patientToAgent', hcaInfo.patientToAgent || '');
      hcaInfoForm.setFieldValue('hasPowerOfAttorney', hcaInfo.hasPowerOfAttorney || 'unset');
      // ALSO UPDATE HCANomination STATE SINCE setFieldValue DOESN'T TRIGGER onChange LISTENER
      setHCANomination(hcaInfo.hasPowerOfAttorney || 'unset');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart?.HEALTHCARE_AGENT_INFO]);

  // INITIALIZE FORM
  const hcaInfoForm = useForm({
    defaultValues: {
      agent: chart?.PATIENT_INFO_1?.firstName ? chart.HEALTHCARE_AGENT_INFO?.agent : '',
      street: chart?.HEALTHCARE_AGENT_INFO?.street ? chart.HEALTHCARE_AGENT_INFO?.street : '',
      agentCity: chart?.HEALTHCARE_AGENT_INFO?.city ? chart.HEALTHCARE_AGENT_INFO?.city : '',
      agentState: chart?.HEALTHCARE_AGENT_INFO?.state ? chart.HEALTHCARE_AGENT_INFO?.state : '',
      agentZIP: chart?.HEALTHCARE_AGENT_INFO?.ZIP ? chart.HEALTHCARE_AGENT_INFO?.ZIP : '',
      agentCountry: chart?.HEALTHCARE_AGENT_INFO?.country ? chart.HEALTHCARE_AGENT_INFO?.country : '',
      agentEmail: chart?.HEALTHCARE_AGENT_INFO?.email ? chart.HEALTHCARE_AGENT_INFO?.email : '',
      // PHONE FIELDS: READ NEW FIELDS WITH FALLBACK TO LEGACY
      primaryPhone: chart?.HEALTHCARE_AGENT_INFO?.primaryPhone
        ? chart.HEALTHCARE_AGENT_INFO?.primaryPhone
        : chart?.HEALTHCARE_AGENT_INFO?.cellPhone
          ? chart.HEALTHCARE_AGENT_INFO?.cellPhone
          : '',
      secondaryPhone: chart?.HEALTHCARE_AGENT_INFO?.secondaryPhone
        ? chart.HEALTHCARE_AGENT_INFO?.secondaryPhone
        : chart?.HEALTHCARE_AGENT_INFO?.homePhone
          ? chart.HEALTHCARE_AGENT_INFO?.homePhone
          : '',
      agentToPatient: chart?.HEALTHCARE_AGENT_INFO?.agentToPatient ? chart.HEALTHCARE_AGENT_INFO?.agentToPatient : '',
      patientToAgent: chart?.HEALTHCARE_AGENT_INFO?.patientToAgent ? chart.HEALTHCARE_AGENT_INFO?.patientToAgent : '',
      hasPowerOfAttorney: chart?.HEALTHCARE_AGENT_INFO?.hasPowerOfAttorney
        ? chart.HEALTHCARE_AGENT_INFO.hasPowerOfAttorney
        : 'unset',
    },
    onSubmit: () => {
      handleHCAInfoUpdate();
    },
  });

  // WRITE FORM DATA TO FIRESTORE
  const handleHCAInfoUpdate = () => {
    // CAPTURE FORM VALUES AT SUBMIT TIME TO PREVENT RACE CONDITIONS
    isSubmittingRef.current = true;
    capturedFormValuesRef.current = { ...hcaInfoForm.state.values };
    const captured = capturedFormValuesRef.current;

    // NORMALIZE PHONE NUMBERS TO E.164 FORMAT BEFORE SAVING
    const primaryPhoneE164 = formatPhoneE164(captured.primaryPhone);
    const secondaryPhoneE164 = formatPhoneE164(captured.secondaryPhone);

    mutate(
      {
        patientId: patientId,
        docData: {
          HEALTHCARE_AGENT_INFO: {
            agent: captured.agent,
            street: captured.street,
            city: captured.agentCity,
            state: captured.agentState,
            ZIP: captured.agentZIP,
            country: captured.agentCountry,
            email: captured.agentEmail,
            // NEW NORMALIZED FIELDS
            primaryPhone: primaryPhoneE164 || captured.primaryPhone,
            secondaryPhone: secondaryPhoneE164 || captured.secondaryPhone,
            // LEGACY FIELDS (FOR BACKWARD COMPATIBILITY)
            cellPhone: primaryPhoneE164 || captured.primaryPhone,
            homePhone: secondaryPhoneE164 || captured.secondaryPhone,
            agentToPatient: captured.agentToPatient,
            patientToAgent: captured.patientToAgent,
            hasPowerOfAttorney: captured.hasPowerOfAttorney,
          },
        },
      },
      {
        onSuccess: () => {
          isSubmittingRef.current = false;
          // alert('Successfully updated document');
          setSubmitted(true);
        },
        onError: (error) => {
          isSubmittingRef.current = false;
          Sentry.captureException(error); // SEND ERROR TO SENTRY
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // WRITE PATIENT INFO ADDRESS VALUES TO HCA INFO FORM
  const handleSameAsPatientCheckboxClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHcaInfoSameAsPatient(!hcaInfoSameAsPatient);
    if (e.target.checked) {
      hcaInfoForm.setFieldValue(
        'street',
        chart?.PATIENT_INFO_2?.streetAddress ? chart?.PATIENT_INFO_2?.streetAddress : '',
      );
      hcaInfoForm.setFieldValue('agentCity', chart?.PATIENT_INFO_2?.city ? chart?.PATIENT_INFO_2?.city : '');
      hcaInfoForm.setFieldValue('agentState', chart?.PATIENT_INFO_2?.state ? chart?.PATIENT_INFO_2?.state : '');
      hcaInfoForm.setFieldValue('agentZIP', chart?.PATIENT_INFO_2?.ZIP ? chart?.PATIENT_INFO_2?.ZIP : '');
      hcaInfoForm.setFieldValue('agentCountry', chart?.PATIENT_INFO_2?.country ? chart?.PATIENT_INFO_2?.country : '');
      // hcaInfoForm.setFieldValue('cellPhone', chart?.PATIENT_INFO_2?.homePhone ? chart?.PATIENT_INFO_2?.homePhone : '');
    } else {
      setHcaInfoSameAsPatient(!hcaInfoSameAsPatient);
      hcaInfoForm.setFieldValue('street', '');
      hcaInfoForm.setFieldValue('agentCity', '');
      hcaInfoForm.setFieldValue('agentState', '');
      hcaInfoForm.setFieldValue('agentZIP', '');
      hcaInfoForm.setFieldValue('agentCountry', '');
      // hcaInfoForm.setFieldValue('cellPhone', '');
    }
  };

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId) {
    return (
      <>
        <div id="hca-info-page" className="flex flex-col">
          <h1 className="place-self-center text-3xl font-bold">HEALTH CARE AGENT INFORMATION</h1>
          <div className="my-4 flex flex-col items-center">
            <div id="hca-info-container" className="flex flex-col items-center">
              <button
                className="ml-2 text-xl text-blue-500 underline"
                onClick={() => {
                  alert(
                    'A healthcare agent is someone who can speak for you during a medical emergency when you cannot speak for yourself.\r\rIf you have not yet named a healthcare agent, you can name one here today, but you should be sure to communicate this to your newly named agent.\r\rIf you are unwilling/unable to name an agent today, you will need to obtain a MIDEO through our guided process. Please call 1-844-MIDEO-4U to speak to a concierge.',
                  );
                }}
              >
                More Info
              </button>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  hcaInfoForm.handleSubmit();
                  window.scrollTo(0, document.body.scrollHeight);
                }}
                className="flex flex-col items-center"
              >
                <div id="same-as-patient" className="flex flex-row items-end">
                  <div className="mb-1 ml-2 flex flex-row items-center rounded-xl border-4 border-solid border-red-500 p-4 text-lg font-bold sm:p-8 sm:text-2xl">
                    <input
                      type="checkbox"
                      id="setAgentInfo"
                      name="setAgentInfo"
                      checked={hcaInfoSameAsPatient}
                      onChange={(e) => handleSameAsPatientCheckboxClick(e)}
                      className="h-8 w-8"
                    />
                    <label className="ml-2" htmlFor="setAgentInfo">
                      Health Care Agent Address Same as Patient
                    </label>
                  </div>
                </div>
                {/* <div id="nav" className="my-4 flex flex-col items-center">
                  {submitted && nextPageURL && <NavForward to={nextPageURL} disabled={false} />}
                </div> */}
                <div id="hca-info-form-inputs" className="ml-0 flex flex-col sm:ml-12">
                  <hcaInfoForm.Field
                    name="agent"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return "Agent's name cannot be empty.";
                        } else if (value.length < 5) {
                          return "Agent's name should contain at least five characters.";
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Agent's Full Name*"
                          id="agent-name"
                          name={field.name}
                          value={field.state.value ? field.state.value : ''}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <>
                    <hcaInfoForm.Field
                      name="street"
                      validators={{
                        onChange({ value }) {
                          if (!value || value.length === 0) {
                            return 'Street address cannot be empty.';
                          } else if (value.length < 5) {
                            return 'Street address should contain at least five characters.';
                          } else {
                            return undefined;
                          }
                        },
                      }}
                      children={(field) => (
                        <>
                          <FormInput
                            label="Agent's Street Address*"
                            id="agent-street-address"
                            name={field.name}
                            value={field.state.value ? field.state.value : ''}
                            handleBlur={field.handleBlur}
                            handleChange={(e) => field.handleChange(e.target.value)}
                            required={true}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                    <hcaInfoForm.Field
                      name="agentCity"
                      validators={{
                        onChange({ value }) {
                          if (!value || value.length === 0) {
                            return 'City cannot be empty.';
                          } else {
                            return undefined;
                          }
                        },
                      }}
                      children={(field) => (
                        <>
                          <FormInput
                            label="Agent's City*"
                            id="agent-city"
                            name={field.name}
                            value={field.state.value ? field.state.value : ''}
                            handleBlur={field.handleBlur}
                            handleChange={(e) => field.handleChange(e.target.value)}
                            required={true}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                    <hcaInfoForm.Field
                      name="agentState"
                      validators={{
                        onChange({ value }) {
                          if (!value || value.length === 0) {
                            return 'State cannot be empty.';
                          } else {
                            return undefined;
                          }
                        },
                      }}
                      children={(field) => (
                        <>
                          <FormInput
                            label="Agent's State*"
                            id="agent-state"
                            name={field.name}
                            value={field.state.value ? field.state.value : ''}
                            handleBlur={field.handleBlur}
                            handleChange={(e) => field.handleChange(e.target.value)}
                            required={true}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                    <hcaInfoForm.Field
                      name="agentZIP"
                      validators={{
                        onChange({ value }) {
                          if (!value || value.length === 0) {
                            return 'ZIP Code cannot be empty.';
                          } else {
                            return undefined;
                          }
                        },
                      }}
                      children={(field) => (
                        <>
                          <FormInput
                            label="Agent's ZIP Code*"
                            id="agent-ZIP"
                            name={field.name}
                            value={field.state.value ? field.state.value : ''}
                            handleBlur={field.handleBlur}
                            handleChange={(e) => field.handleChange(e.target.value)}
                            required={true}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                    <hcaInfoForm.Field
                      name="agentCountry"
                      validators={{
                        onChange({ value }) {
                          if (!value || value.length === 0) {
                            return 'Country cannot be empty.';
                          } else {
                            return undefined;
                          }
                        },
                      }}
                      children={(field) => (
                        <>
                          <FormInput
                            label="Agent's Country*"
                            id="agent-country"
                            name={field.name}
                            value={field.state.value ? field.state.value : ''}
                            handleBlur={field.handleBlur}
                            handleChange={(e) => field.handleChange(e.target.value)}
                            required={true}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                    <h2 className="text-xl font-bold">Agent&apos;s Primary Phone</h2>
                    <hcaInfoForm.Field
                      name="primaryPhone"
                      validators={{
                        onChange: ({ value }) => {
                          if (!value) return 'Primary Phone is required';
                          const result = validatePhone(value);
                          return result.isValid ? undefined : result.error;
                        },
                      }}
                      children={(field) => (
                        <>
                          <PhoneInput
                            international
                            defaultCountry="US"
                            placeholder="Enter phone number"
                            value={field.state.value ? field.state.value : ''}
                            onChange={(value: string | undefined) => field.handleChange(value || '')}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                    <h2 className="text-xl font-bold">Agent&apos;s Secondary Phone (Optional)</h2>
                    <hcaInfoForm.Field
                      name="secondaryPhone"
                      children={(field) => (
                        <>
                          <PhoneInput
                            international
                            defaultCountry="US"
                            placeholder="Enter phone number"
                            value={field.state.value ? field.state.value : ''}
                            onChange={(value: string | undefined) => field.handleChange(value || '')}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                    {hcaLinkedRequest &&
                      (hcaLinkedRequest.status === 'waiting' ||
                        hcaLinkedRequest.status === 'accepted') && (
                        <hcaInfoForm.Subscribe
                          selector={(state) => state.values.agentEmail}
                          children={(agentEmail) =>
                            !emailsMatch(agentEmail, hcaLinkedRequest.recipientEmail) ? (
                              <p
                                className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
                                role="alert"
                              >
                                Changing this email will disconnect this Healthcare Agent&rsquo;s
                                active invitation — they will stop receiving alerts. After saving,
                                return to My MIDEO Family to re-invite them.
                              </p>
                            ) : null
                          }
                        />
                      )}
                    <hcaInfoForm.Field
                      name="agentEmail"
                      validators={{
                        onChange({ value }) {
                          if (!value || value.length === 0) {
                            return 'Email cannot be empty.';
                          }
                          // Belt-and-suspenders: catch an obviously-invalid
                          // address before submit so the patient fixes it
                          // without a round-trip. The cloud function is the hard
                          // gate (rejects with invalid-argument), but a
                          // malformed email is the exact failure that used to
                          // silently orphan the invite (MIDEO-UNIFIED-18).
                          if (!isValidEmail(value)) {
                            return 'Please enter a valid email address.';
                          }
                          return undefined;
                        },
                      }}
                      children={(field) => (
                        <>
                          <FormInput
                            label="Agent's Email Address*"
                            id="agentEmail"
                            name={field.name}
                            value={field.state.value ? field.state.value : ''}
                            handleBlur={field.handleBlur}
                            handleChange={(e) => field.handleChange(e.target.value)}
                            required={true}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                    <hcaInfoForm.Field
                      name="agentToPatient"
                      validators={{
                        onChange({ value }) {
                          if (!value || value.length === 0) {
                            return "Agent's relationship to patient cannot be empty.";
                          } else {
                            return undefined;
                          }
                        },
                      }}
                      children={(field) => (
                        <>
                          <FormInput
                            label="Agent's Relationship To Patient*"
                            id="agent-to-patient"
                            placeholder='This person is my "___"'
                            name={field.name}
                            value={field.state.value ? field.state.value : ''}
                            handleBlur={field.handleBlur}
                            handleChange={(e) => field.handleChange(e.target.value)}
                            required={true}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                    <hcaInfoForm.Field
                      name="patientToAgent"
                      validators={{
                        onChange({ value }) {
                          if (!value || value.length === 0) {
                            return "Patient's relationship to agent cannot be empty.";
                          } else {
                            return undefined;
                          }
                        },
                      }}
                      children={(field) => (
                        <>
                          <FormInput
                            label="Patient's Relationship To Agent*"
                            id="patient-to-agent"
                            placeholder='My agent calls me their "___"'
                            name={field.name}
                            value={field.state.value ? field.state.value : ''}
                            handleBlur={field.handleBlur}
                            handleChange={(e) => field.handleChange(e.target.value)}
                            required={true}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                  </>
                </div>
                <div id="hca-nomination" className="ml-0 flex flex-col sm:ml-12">
                  <hcaInfoForm.Field
                    name="hasPowerOfAttorney"
                    listeners={{
                      onMount: ({ value }) => {
                        setHCANomination(value);
                      },
                      onChange: ({ value }) => {
                        setHCANomination(value);
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormSelect
                          label="HCA Info*"
                          name="hca-info"
                          id="power-of-attorney-select"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                          options={[
                            {
                              name: '--Please choose an option--',
                              value: 'unset',
                            },
                            {
                              name: 'I already appointed my HCA in a legal Healthcare Power of Attorney document',
                              value: 'yes',
                            },
                            {
                              name: 'I am using the MIDEO process to designate this person as my HCA ',
                              value: 'no',
                            },
                          ]}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                </div>
                <div id="more-hca-info" className="ml-0 flex flex-col sm:ml-12">
                  {HCANomination === 'yes' && (
                    <div className="flex flex-col items-center">
                      <h2 className="text-xl font-bold">
                        Please provide a copy of the Healthcare Power of Attorney document
                      </h2>
                      <div>
                        {chart?.OTHER_DOCUMENTS?.powerOfAttorneyURI?.startsWith('https://') ? (
                          <>
                            <a
                              className="font-medium text-blue-600 hover:underline dark:text-blue-500"
                              href={chart?.OTHER_DOCUMENTS?.powerOfAttorneyURI}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <p className="text-xl">View current medical durable power of attorney</p>
                            </a>
                          </>
                        ) : (
                          <>
                            <Uploader
                              docToUpdate="OTHER_DOCUMENTS"
                              bucket="patient"
                              type={FileTypes.powerOfAttorney}
                              title="Upload Medical Durable Power of Attorney"
                              elementId="powerOfAttorney-file-upload"
                            />
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div id="form-submit-button" className="mt-8 flex flex-col items-center">
                  <hcaInfoForm.Subscribe
                    selector={(state) => [state.canSubmit]}
                    children={([canSubmit]) => (
                      <>
                        {!canSubmit && (
                          <div className="mt-4 flex flex-col items-center">
                            <p className="text-red-500">Please correct any errors above before re-submitting.</p>
                          </div>
                        )}
                        <Button
                          id="submit-hca-info"
                          type="submit"
                          disabled={
                            !canSubmit ||
                            HCANomination === 'unset' ||
                            (HCANomination === 'yes' && !chart?.OTHER_DOCUMENTS?.powerOfAttorneyURI)
                          }
                          onClick={hcaInfoForm.handleSubmit}
                          textContent="Submit"
                          submitted={submitted}
                        />
                      </>
                    )}
                  />
                </div>
              </form>
            </div>
          </div>
          <div id="nav" className="my-4 flex flex-col items-center gap-3">
            {submitted && (
              <div className="flex flex-col items-center">
                <button
                  id="submit-form-info"
                  title="Submit Form Info"
                  onClick={handleNextButtonClick}
                  disabled={false}
                  className={classNames.nextButton}
                >
                  Next
                </button>
              </div>
            )}
            {/* ECN convergence (CU-86aj2q4fc): once the HCA record is complete,
                cross-link to the hub where the invite fires (page-concern
                separation). Dynamic text: "manage" when already invited, else
                "invite". Gated on the SAVED record. */}
            {isChartRecordComplete(chart?.HEALTHCARE_AGENT_INFO) && (
              <button
                id="go-to-hub-hca"
                title="Go to My MIDEO Family"
                onClick={() => navigate({ to: '/myMideoFamily' })}
                className="rounded border-2 border-mideo-button-dark-green px-4 py-2 font-bold text-mideo-button-dark-green transition-all hover:bg-mideo-button-dark-green hover:text-white"
              >
                {hcaLinkedRequest &&
                (hcaLinkedRequest.status === 'waiting' || hcaLinkedRequest.status === 'accepted')
                  ? `Manage ${chart?.HEALTHCARE_AGENT_INFO?.agent || 'this agent'}'s alerts in My MIDEO Family`
                  : `Invite ${chart?.HEALTHCARE_AGENT_INFO?.agent || 'this agent'} to receive alerts`}
              </button>
            )}

            {/* ECN Subtask 9: opt-in entry to alternate agents. Enables the
                navObj-disabled alternateAgents screen for this patient (so it
                becomes menu-visible) and navigates there. */}
            <button
              id="add-alternate-agents"
              title="Add alternate agents"
              onClick={() => {
                if (patientInfo) {
                  setDisabledPage({ name: 'alternateAgents', value: false, patientInfo });
                }
                navigate({
                  to: '/$patientId/careTeam/alternateAgents',
                  params: { patientId },
                });
              }}
              className="rounded border-2 border-mideo-button-dark-green px-4 py-2 font-bold text-mideo-button-dark-green transition-all hover:bg-mideo-button-dark-green hover:text-white"
            >
              Add alternate agents
            </button>
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
